import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import styles from "./ContactUs.module.css";
import { RootState } from "../../redux/rootReducer";
import { useDispatch, useSelector } from "react-redux";
import { ContactForm, sendEmail } from "../../redux/reducers/contactFormSES";
import { AppDispatch } from "../../redux/store";

interface ContactUsPageProps {
  isImageFullScreen: boolean;
  setIsImageFullScreen: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

const ContactUsPage: React.FC<ContactUsPageProps> = ({ closeModal }) => {
  const [contactForm, setContactForm] = useState<ContactForm>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const status = useSelector((state: RootState) => state.contactForm?.status); // Access status property on the contactForm slice

  const error = useSelector((state: RootState) => state.contactForm?.error); // Access error property on the contactForm slice

  useEffect(() => {
    if (status === "succeeded") {
      setIsSuccess(true);
      setIsDisabled(true);
      setTimeout(() => {
        setIsSuccess(false);
        setIsDisabled(false);
        closeModal();
      }, 5000);
    }
  }, [status, closeModal]);

  const handleInput = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = event.target;
    setContactForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Validating name
    if (contactForm.name.trim() === "") {
      alert("Name is required");
      return;
    }

    // Validating email
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
    if (!emailPattern.test(contactForm.email.trim())) {
      alert("Please enter a valid email address");
      return;
    }

    // Validating message
    if (contactForm.message.trim().length < 10) {
      alert("Message must be at least 10 characters long");
      return;
    }

    try {
      const actionResult = await dispatch(sendEmail(contactForm));

      if (actionResult) {
        setFormSubmitted(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.contactUsContainer}>
      <div className={styles.contactUsBox}>
        <div className={styles.closeButton} onClick={closeModal}>
          X
        </div>
        <h1 className={styles.contactUsTitle}>Contact Us</h1>
        <div className={styles.contactUsColumn}>
          <div className={styles.contactInputBox}>
            {formSubmitted ? (
              <div className={styles.successMessage}>
                Your message has been sent successfully!
              </div>
            ) : (
              <form onSubmit={handleSubmit} className={styles.contactform}>
                <div className={styles.inputFieldRow}>
                  <label className={styles.contactLabel} htmlFor="name">
                    Name:
                  </label>
                  <input
                    className={styles.contactInputField}
                    type="text"
                    id="name"
                    name="name"
                    value={contactForm.name}
                    onChange={handleInput}
                    required
                  />
                </div>
                <div className={styles.inputFieldRow}>
                  <label className={styles.contactLabel} htmlFor="phone">
                    Phone:
                  </label>
                  <input
                    className={styles.contactInputField}
                    type="tel"
                    id="phone"
                    name="phone"
                    value={contactForm.phone}
                    onChange={handleInput}
                  />
                </div>
                <div className={styles.inputFieldRow}>
                  <label className={styles.contactLabel} htmlFor="email">
                    Email:
                  </label>
                  <input
                    className={styles.contactInputField}
                    type="email"
                    id="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleInput}
                    pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                    required
                  />
                </div>
                <div className={styles.inputFieldRow}>
                  <label className={styles.contactLabel} htmlFor="message">
                    Message:
                  </label>
                  <textarea
                    className={styles.contactMessage}
                    id="message"
                    name="message"
                    rows={5}
                    value={contactForm.message}
                    onChange={handleInput}
                    required
                  />
                </div>
                {error && <div className={styles.error}>{error}</div>}
                <button
                  type="submit"
                  className={styles.contactSubmitButton}
                  disabled={isDisabled}
                >
                  Send
                </button>
                {isSuccess && (
                  <div className={styles.success}>
                    Email has been successfully sent!
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;
